import { cn } from '@/lib/cn';
import type { ReactNode } from 'react';
import { LogoMark } from './LogoMark';

/**
 * Wspolny ekran ladowania z marka (Dawid 2026-09-30: „bardziej premium, nowy,
 * zeby bylo widac, ze jest zmienione”). Jeden wyglad dla: logowania (AuthGuard),
 * powitania/wylogowania (SplashScreen). Maly wariant paska — `BrandProgressBar`
 * — dla przejsc miedzy stronami (RouteLoader).
 *
 * Elementy: logo wjezdza od dolu, gruby pasek postepu z przesuwajacym sie
 * polyskiem, podpis. (Zlota poswiata za logo usunieta — Dawid 2026-09-30.) Animacje wylaczone przy
 * `prefers-reduced-motion` (globals.css).
 *
 * `durationMs` — pasek dochodzi do 100% w tym czasie (powitanie/wylogowanie).
 * Bez `durationMs` pasek „pelznie” do ~90% i czeka (nie wiemy, ile potrwa).
 */
export function BrandLoader({
  durationMs,
  tagline = 'Platforma CoreLTB Builders',
  status,
  greeting,
  message,
  className,
}: {
  durationMs?: number;
  tagline?: string;
  /** Krotki tekst pod paskiem, np. „Wylogowywanie…”. */
  status?: string;
  /** Codzienne powitanie, np. „Cześć, Julia!” (DailyWelcome). */
  greeting?: ReactNode;
  /** Haslo dnia pod powitaniem. */
  message?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-bg',
        className,
      )}
      role="status"
      aria-live="polite"
      aria-label={status ?? 'Ładowanie'}
    >
      <div className="relative flex flex-col items-center px-8">
        <div className="brand-rise">
          <LogoMark size="lg" />
        </div>

        {tagline && (
          <p className="brand-rise mt-5 text-sm font-medium text-fg-muted [animation-delay:120ms]">
            {tagline}
          </p>
        )}

        {greeting && (
          <p className="brand-rise mt-8 text-center text-2xl font-semibold tracking-tight text-fg [animation-delay:250ms] sm:text-3xl">
            {greeting}
          </p>
        )}
        {message && (
          <p className="brand-rise mt-3 max-w-md text-balance text-center text-base leading-relaxed text-fg-muted [animation-delay:450ms]">
            {message}
          </p>
        )}

        <div className="brand-rise mt-9 w-60 sm:w-80 [animation-delay:220ms]">
          <BrandProgressBar durationMs={durationMs} />
          {status && <p className="mt-3 text-center text-xs text-fg-subtle">{status}</p>}
        </div>
      </div>
    </div>
  );
}

/** Pasek postepu marki — samodzielnie w RouteLoaderze, w srodku BrandLoadera. */
export function BrandProgressBar({
  durationMs,
  className,
}: {
  durationMs?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'relative h-1 w-full overflow-hidden rounded-full bg-fg/10 dark:bg-fg/15',
        className,
      )}
    >
      <div
        className={cn(
          'relative h-full overflow-hidden rounded-full bg-gradient-to-r from-accent/70 via-accent to-accent-hover',
          durationMs ? 'brand-fill' : 'brand-creep',
        )}
        style={durationMs ? { animationDuration: `${durationMs}ms` } : undefined}
      >
        {/* Polysk przesuwajacy sie po wypelnieniu */}
        <span
          aria-hidden
          className="brand-shine absolute inset-y-0 left-0 w-1/2 bg-gradient-to-r from-transparent via-white/70 to-transparent"
        />
      </div>
    </div>
  );
}
